/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Configuration, FetchInit} from '@adobe/exc-app/network';
import {Level} from '@adobe/exc-app/metrics/Level';
import metrics from '../metrics';

const fetchConfig = {
  getTokenInfo: (): Promise<{token: string}> => Promise.reject('Api called without token'),
  imsClientId: '',
  imsOrg: '',
  sandbox: {isDefault: true, name: '', type: ''}
};

const logger = metrics.create('exc.module-runtime.network.fetch');

function configure(config: Configuration): void {
  Object.assign(fetchConfig, config);
}

function updateReq(input: RequestInfo, init?: FetchInit): Promise<{input: RequestInfo; init?: FetchInit}> {
  return fetchConfig.getTokenInfo().then(({token}) => {
    if (init && init.auth) {
      const {imsClientId, imsOrg, sandbox} = fetchConfig;
      let metadata = {imsClientId, imsOrg, sandbox, token};
      init.metadata && Object.assign(metadata, init.metadata);
      if (!(init.headers instanceof Headers)) {
        init.headers = new Headers(init.headers);
      }
      if (init.auth === 'Header') {
        init.headers.set('Authorization', `Bearer ${metadata.token}`);
        init.headers.set('x-api-key', metadata.imsClientId);
        metadata.imsOrg && init.headers.set('x-gw-ims-org-id', metadata.imsOrg);
        init.excludeSandboxHeaders || metadata.sandbox && init.headers.set('x-sandbox-name', metadata.sandbox.name);
        init.excludeSandboxHeaders || metadata.sandbox && init.headers.set('x-sandbox-type', metadata.sandbox.type);
        init.excludeSandboxHeaders || metadata.sandbox && init.headers.set('x-sandbox-default', metadata.sandbox.isDefault.toString());
      } else if (init.auth === 'Body') {
        // Adding header always to enable Adobe IO token validation.
        init.headers.set('Authorization', `Bearer ${metadata.token}`);
        init.headers.set('x-api-key', metadata.imsClientId);
        let tokenParam = {apiKey: metadata.imsClientId, userToken: metadata.token};
        metadata.imsOrg && Object.assign(tokenParam, {imsOrgId: metadata.imsOrg});
        metadata.sandbox && Object.assign(tokenParam, {sandboxName: metadata.sandbox.name});
        metadata.sandbox && Object.assign(tokenParam, {sandboxType: metadata.sandbox.type});
        metadata.sandbox && Object.assign(tokenParam, {isDefaultSandbox: metadata.sandbox.isDefault});
        if (!init.body) {
          init.body = JSON.stringify(tokenParam);
        } else {
          let parsedBody = JSON.parse(init.body as string);
          Object.assign(parsedBody, tokenParam);
          init.body = JSON.stringify(parsedBody);
        }
      }
    }
    return {init, input};
  });
}

function fetchApi(input: RequestInfo, init?: FetchInit, ioEndpoint?: string): Promise<Response> {
  return updateReq(input, init).then(async ({init, input}) => {
    //populate network-runtime option to differentiate network runtime fetch.
    try {
      return await fetch(input, Object.assign({}, init, {'network-runtime': true}));
    } catch (error) {
      if (ioEndpoint) {
        logger.event('Fetch.fallback',
          'Primary call failed. Retrying with secondary domain', {
            endpoint: input instanceof Request ? input.url : input,
            ioEndpoint
          },
          {level: Level.WARN});
        return await fetch(ioEndpoint, Object.assign({}, init, {'network-runtime': true}));
      }
      throw error;
    }
  }).catch(err => {
    if (ioEndpoint) {
      logger.event('Fetch.fallbackFailed',
        'Failed to call both Primary and Secondary domain', {
          endpoint: input instanceof Request ? input.url : input,
          ioEndpoint
        },
        {level: Level.ERROR});
    }
    return Promise.reject(`error in making a fetch call. Error is:- ${err}`);
  });
}

export default {
  configure,
  fetch: fetchApi,
  updateReq
};

